/* Everything that talks to Supabase. The browser only ever has the publishable (anon) key; Row Level Security in the
   database decides what each person may read or change, so a refused write comes back as an error shown to the user. */
import {createClient} from '@supabase/supabase-js';
import {assemble, entryFromRow, entryToRow, ENTRY_COLS, BEGINNING, DEFAULT_COST, type Assignment, type Data, type Entry, type Member, type Client, type Project, type Timer, type Rows} from './lib/model.ts';
import {indexExisting, type ImportPlan, type Existing} from './lib/importer.ts';
import type {ServerGroup, ServerTotal} from './lib/report.ts';
import {isRetryable, type Op, type Deletion} from './lib/sync.ts';
import {opMadeAt, type MergeNote} from './lib/merge.ts';
import {TABLE_COLS, orderOf, archivedRolePlan, type Tables} from './lib/backup.ts';

const env = (import.meta as any).env ?? (globalThis as any).process?.env ?? {};   // Vite in the browser; process.env when tests run db.ts in Node
const url = env.VITE_SUPABASE_URL as string | undefined;
const key = env.VITE_SUPABASE_ANON_KEY as string | undefined;
export const configured = !!(url && key);
/** every request gives up after a while (a phone with one bar of signal must not wait forever); callers retry later */
const TIMEOUT_MS = 30000;
/** resolves when the device reports a connection again (or after ms) */
const whenOnline = (ms: number) => new Promise<void>(r => { const done = () => { clearTimeout(t); removeEventListener('online', done); r(); }; const t = setTimeout(done, ms); addEventListener('online', done); });
async function fetchWithTimeout(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // refreshing the login while the device says it is offline: wait for the connection instead of failing at once. The
  // login library retries a failed refresh with growing pauses (up to ~13 s) and makes everything else wait for it, so
  // after the signal came back changes could wait that long to be sent; now the refresh goes out the moment it is back.
  if (typeof navigator !== 'undefined' && navigator.onLine === false && String(input instanceof Request ? input.url : input).includes('/auth/v1/token?grant_type=refresh_token')) await whenOnline(20000);
  if (init?.signal) return fetch(input, init);
  const c = new AbortController(), t = setTimeout(() => c.abort(), TIMEOUT_MS);
  return fetch(input, {...init, signal: c.signal}).finally(() => clearTimeout(t));
}
export const sb = createClient(url || 'http://localhost', key || 'missing', {
  auth: {persistSession: true, autoRefreshToken: true},
  global: {fetch: fetchWithTimeout},
  realtime: {params: {eventsPerSecond: 20}},
});

/** whose login is saved on this device (the login library's own stored copy, read directly): the app can open from the
    device's data at once, without waiting for the login library to start — which, with an expired access token and no
    signal, first retries refreshing it for about half a minute (owner report 2026-10-29: the app did not open offline).
    Never treated as proof of a valid login: the server is only asked once the library confirms it. */
export function storedLoginUser(): string | null {
  try {
    const k = 'sb-' + new URL(url || 'http://localhost').hostname.split('.')[0] + '-auth-token';
    const v = JSON.parse(localStorage.getItem(k) || 'null');
    return v && typeof v.refresh_token === 'string' && typeof v.user?.id === 'string' ? v.user.id : null;
  } catch { return null; }
}

function check<T>(r: {data: T; error: any}): T { if (r.error) throw new Error(r.error.message || String(r.error)); return r.data; }
const PAGE = 1000;   // the Data API returns at most 1,000 rows per request

/** every row of a table, fetched in pages (in parallel, a few at a time), ordered by its key so pages never overlap */
async function fetchAll(table: string, cols: string, order: string, onProgress?: (n: number, total: number) => void, filter: (q: any) => any = q => q): Promise<any[]> {
  const {count, error} = await filter(sb.from(table).select(order, {count: 'exact', head: true}));   // order: one or more columns (a unique key)
  if (error) throw new Error(table + ': ' + error.message);
  const total = count || 0, pages = Math.ceil(total / PAGE), out: any[][] = new Array(pages);
  let next = 0, done = 0;
  const worker = async () => {
    while (next < pages) {
      const i = next++;
      let q = filter(sb.from(table).select(cols));
      for (const c of order.split(',')) q = q.order(c);
      out[i] = check(await q.range(i * PAGE, i * PAGE + PAGE - 1)) as any[];
      done += out[i].length; onProgress?.(done, total);
    }
  };
  await Promise.all(Array.from({length: Math.min(6, pages)}, worker));
  const rows = out.flat();
  if (rows.length !== total) throw new Error(`${table}: expected ${total} rows, received ${rows.length} — reload to try again`);
  return rows;
}

export async function loadStructure(): Promise<Omit<Data, 'entries'> & {assign: Assignment[]}> {
  const t = (name: string, cols: string, order: string) => fetchAll(name, cols, order);
  const [members, member_money, member_cost_rates, clients, client_contacts, projects, project_rates, tasks, pm] = await Promise.all([
    t('members', 'id,name,email,role,active,auth_user_id,import_aliases,created_at,archived_role', 'id'), t('member_money', 'member_id,bill_rate', 'member_id'),
    t('member_cost_rates', 'member_id,effective_from,rate', 'member_id'), t('clients', 'id,name,active,aliases,created_at', 'id'),
    t('client_contacts', 'client_id,email,about', 'client_id'), t('projects', 'id,client_id,name,default_billable,active,start_date,created_at', 'id'),
    t('project_rates', 'project_id,bill_rate', 'project_id'), t('tasks', 'id,project_id,name,active,created_at', 'id'),
    t('project_members', 'project_id,member_id', 'project_id,member_id'),   // RLS: owner/admin all; everyone else their own
  ]);
  return {...assemble({members, member_money, member_cost_rates, clients, client_contacts, projects, project_rates, tasks} as Rows),
          assign: pm.map(r => ({projectId: r.project_id, memberId: r.member_id}))};
}
/** entries on or after a date (the recent weeks every device keeps; older ones stay on the server) */
export async function loadEntries(onProgress?: (n: number, total: number) => void, fromDate?: string): Promise<Entry[]> {
  return (await fetchAll('time_entries', ENTRY_COLS, 'id', onProgress, q => fromDate ? q.gte('work_date', fromDate) : q)).map(entryFromRow);
}
/** a page of entries before a date, newest first (Entries tab: "Load older entries") */
export async function olderEntries(beforeDate: string, offset: number, limit = 200, memberId?: string): Promise<Entry[]> {
  let q = sb.from('time_entries').select(ENTRY_COLS).lt('work_date', beforeDate);
  if (memberId) q = q.eq('member_id', memberId);   // only this person's (the Entries page shows your own unless you choose Everyone)
  return (check(await q
    .order('work_date', {ascending: false}).order('started_wall', {ascending: false}).order('id').range(offset, offset + limit - 1)) as any[]).map(entryFromRow);
}
const timerFromRow = (t: any): Timer => ({memberId: t.member_id, projectId: t.project_id || '', taskId: t.task_id || '', description: t.description || '', billable: !!t.billable, startedAt: t.started_at});
export async function loadTimers(): Promise<Timer[]> {
  return (check(await sb.from('active_timers').select('*')) as any[]).map(timerFromRow);
}
/** a device's regular catch-up in one small request (public.sync_state): changed entries (more = over 2000, page them
    instead), deletion notes, running timers, the entry count for the self-check, and the lists' fingerprint */
export async function syncState(entriesSince: string, deletionsSince: string, windowFrom: string):
    Promise<{entries: Entry[]; more: boolean; deletions: Deletion[]; timers: Timer[]; count: number; stamp: string}> {
  const r = check(await sb.rpc('sync_state', {entries_since: entriesSince, deletions_since: deletionsSince, window_from: windowFrom})) as any;
  return {entries: (r.entries || []).map(entryFromRow), more: (r.entries || []).length > 2000, deletions: r.deletions || [], timers: (r.timers || []).map(timerFromRow),
          count: Number(r.count), stamp: r.stamp};
}
/** the team-member id linked to this login (RLS lets everyone read their own row); null = not linked or archived */
export async function myMemberId(authUserId: string): Promise<string | null> {
  const rows = check(await sb.from('members').select('id').eq('auth_user_id', authUserId)) as any[];
  return rows.length ? rows[0].id : null;
}

/* ---------- this person's time zone (member_prefs, migration 014: a display preference; RLS: own row only) ---------- */
/** the zone saved for this person; null = none saved yet (Chicago) */
export async function myTimeZone(memberId: string): Promise<string | null> {
  const rows = check(await sb.from('member_prefs').select('time_zone').eq('member_id', memberId)) as any[];
  return rows.length ? rows[0].time_zone : null;
}
/** save it, and read it back (the database refuses an unknown zone, or anyone else's row) */
export async function saveMyTimeZone(memberId: string, tz: string): Promise<string> {
  check(await sb.from('member_prefs').upsert({member_id: memberId, time_zone: tz}, {onConflict: 'member_id'}));
  const back = await myTimeZone(memberId);
  if (back !== tz) throw new Error('The time zone was not saved.');
  return back;
}

/* ---------- birthdays (migration 017, a Little surprise; see birthdays.ts). RLS: own row only, for everyone ---------- */
/** this person's list and its version (null = none saved yet). Throws Offline (retry later) or Error. */
export async function myBirthdays(memberId: string): Promise<{data: unknown; rev: number} | null> {
  const r = await sb.from('member_birthdays').select('data, rev').eq('member_id', memberId);
  if (r.error) fail(r);
  const rows = (r.data || []) as any[];
  return rows.length ? {data: rows[0].data, rev: Number(rows[0].rev)} : null;
}
/** save the list, only over the version this device last saw (rev; null = none there yet). Returns the new version, or
    null when someone else (another device) saved in between: then read, merge and try again. */
export async function saveBirthdays(memberId: string, data: object, rev: number | null): Promise<number | null> {
  const r = rev === null
    ? await sb.from('member_birthdays').insert({member_id: memberId, data}).select('rev')
    : await sb.from('member_birthdays').update({data}).eq('member_id', memberId).eq('rev', rev).select('rev');
  if (r.error) { if (rev === null && (r.error as any).code === '23505') return null; fail(r); }
  const rows = (r.data || []) as any[];
  return rows.length ? Number(rows[0].rev) : null;
}

/* ---------- "Also running" switches (migration 015; see alsorun.ts). A screen setting only: who may read timers is unchanged ---------- */
/** this person's own switch (no row = On) and the company's (no row = On) */
export async function alsoRunningPrefs(memberId: string): Promise<{mine: boolean; company: boolean}> {
  const [p, c] = await Promise.all([sb.from('member_prefs').select('show_others_running').eq('member_id', memberId),
                                    sb.from('company_settings').select('show_also_running')]);
  const pr = check(p) as any[], cr = check(c) as any[];
  return {mine: pr.length ? pr[0].show_others_running !== false : true, company: cr.length ? cr[0].show_also_running !== false : true};
}
/** save this person's own switch, and read it back (the database refuses anyone else's row) */
export async function saveShowOthersRunning(memberId: string, on: boolean): Promise<boolean> {
  check(await sb.from('member_prefs').upsert({member_id: memberId, show_others_running: on}, {onConflict: 'member_id'}));
  const rows = check(await sb.from('member_prefs').select('show_others_running').eq('member_id', memberId)) as any[];
  if (!rows.length || rows[0].show_others_running !== on) throw new Error('The switch was not saved.');
  return on;
}
/** the company switch (owner, admins only: anyone else changes no row), read back */
export async function saveCompanyAlsoRunning(on: boolean): Promise<boolean> {
  const rows = check(await sb.from('company_settings').update({show_also_running: on}).eq('id', true).select('show_also_running')) as any[];
  if (rows.length !== 1 || rows[0].show_also_running !== on) throw new Error('Only the owner or an admin can change this.');
  return on;
}

/* ---------- time entries and timers: written through the sync queue (sendOp below) ---------- */

/* ---------- team ---------- */
/** name/email/active (+ role, owner only) on members; billing rate in member_money (no row = not set);
    cost history in member_cost_rates (new rows written first, then old ones removed, so a failure never leaves none) */
export async function saveMember(m: Member, isNew: boolean, includeRole: boolean): Promise<void> {
  const row: any = {name: m.name, email: m.email || null, active: m.active};
  if (includeRole) row.role = m.role;
  if (isNew) check(await sb.from('members').insert({id: m.id, ...row, role: m.role}));
  else {
    const rows = check(await sb.from('members').update(row).eq('id', m.id).select('id')) as any[];
    if (rows.length !== 1) throw new Error('This person could not be changed with your role.');
  }
  if (m.billRate === null) check(await sb.from('member_money').delete().eq('member_id', m.id));
  else check(await sb.from('member_money').upsert({member_id: m.id, bill_rate: m.billRate}));
  // no rows = cost unknown (not $0): then every old row goes
  const cost = m.costRates.map(r => ({member_id: m.id, effective_from: r.from || BEGINNING, rate: r.rate}));
  if (cost.length) check(await sb.from('member_cost_rates').upsert(cost));
  const keep = cost.map(r => r.effective_from);
  const old = sb.from('member_cost_rates').delete().eq('member_id', m.id);
  check(await (keep.length ? old.not('effective_from', 'in', '(' + keep.join(',') + ')') : old));
}
export async function setRole(memberId: string, role: string): Promise<void> {
  const rows = check(await sb.from('members').update({role}).eq('id', memberId).select('id')) as any[];
  if (rows.length !== 1) throw new Error('Role not changed.');
}
export async function deleteMember(id: string): Promise<void> {
  const r = await sb.from('members').delete().eq('id', id).select('id');
  if (r.error?.code === '23503') throw new Error('This person has time entries, so they cannot be deleted. Archive them instead (history is kept).');
  if ((check(r) as any[]).length !== 1) throw new Error('Only the owner can delete people.');
}

/* ---------- clients / projects / tasks ---------- */
async function saveContact(c: Pick<Client, 'id' | 'email' | 'about'>): Promise<void> {
  if (!c.email && !c.about) check(await sb.from('client_contacts').delete().eq('client_id', c.id));
  else check(await sb.from('client_contacts').upsert({client_id: c.id, email: c.email, about: c.about}));
}
export async function saveClient(c: Client, isNew: boolean): Promise<void> {
  const row = {id: c.id, name: c.name, active: c.active, aliases: c.aliases};
  if (isNew) check(await sb.from('clients').insert(row));
  else check(await sb.from('clients').update(row).eq('id', c.id));
  await saveContact(c);
}
export async function saveProject(p: Project, isNew: boolean): Promise<void> {
  const row = {id: p.id, client_id: p.clientId, name: p.name, active: p.active, default_billable: p.defaultBillable, start_date: p.startDate};
  if (isNew) check(await sb.from('projects').insert(row));
  else check(await sb.from('projects').update(row).eq('id', p.id));
  // NULL (no row) and 0 are different: blank = not decided, 0 = billable at $0
  if (p.billableRate === null) check(await sb.from('project_rates').delete().eq('project_id', p.id));
  else check(await sb.from('project_rates').upsert({project_id: p.id, bill_rate: p.billableRate}));
}
/** archive (active = false) or restore a project or a client; read back so a refused change is not reported as done */
export async function setActive(table: 'projects' | 'clients', id: string, active: boolean): Promise<void> {
  const rows = check(await sb.from(table).update({active}).eq('id', id).select('id,active')) as any[];
  if (rows.length !== 1 || rows[0].active !== active) throw new Error('Only the owner or an admin can archive or restore this.');
}
/** a project's People: who may record time on it (owner/admin only; the database refuses anyone else) */
export async function setProjectPeople(projectId: string, add: string[], remove: string[]): Promise<void> {
  if (add.length) check(await sb.from('project_members').upsert(add.map(m => ({project_id: projectId, member_id: m})), {onConflict: 'project_id,member_id', ignoreDuplicates: true}));
  if (remove.length) check(await sb.from('project_members').delete().eq('project_id', projectId).in('member_id', remove));
}
export async function addTask(id: string, projectId: string, name: string): Promise<void> { check(await sb.from('tasks').insert({id, project_id: projectId, name})); }
/** how many time entries use a project / task (the database counts them; deleting is refused when any do) */
export async function entriesUsing(col: 'project_id' | 'task_id', ids: string[]): Promise<number> {
  if (!ids.length) return 0;
  const {count, error} = await sb.from('time_entries').select('id', {count: 'exact', head: true}).in(col, ids);
  if (error) throw new Error(error.message);
  return count || 0;
}
export async function deleteRows(table: 'tasks' | 'projects' | 'clients', ids: string[]): Promise<void> {
  if (!ids.length) return;
  const rows = check(await sb.from(table).delete().in('id', ids).select('id')) as any[];
  if (rows.length !== ids.length) throw new Error('Only ' + rows.length + ' of ' + ids.length + ' could be deleted.');
}
export async function tidyClient(c: Client, name: string, email: string, about: string): Promise<void> {
  check(await sb.from('clients').update({name, aliases: Array.from(new Set([...c.aliases, c.name]))}).eq('id', c.id));
  await saveContact({id: c.id, email, about});
}
export async function mergeClients(targetId: string, sourceIds: string[]): Promise<any> {
  return check(await sb.rpc('merge_clients', {target_id: targetId, source_ids: sourceIds}));
}

/* ---------- server functions (Cloudflare Pages Functions; they hold the service key, the browser never does) ---------- */
export async function callFunction(path: string, body: unknown, auth = true): Promise<any> {
  const headers: Record<string, string> = {'content-type': 'application/json'};
  if (auth) { const {data} = await sb.auth.getSession(); if (data.session) headers.authorization = 'Bearer ' + data.session.access_token; }
  const res = await fetch('/api/' + path, {method: 'POST', headers, body: JSON.stringify(body)});
  let j: any = null; try { j = await res.json(); } catch { /* not json */ }
  if (!res.ok) throw new Error((j && j.error) || ('Server error ' + res.status));
  return j;
}

/* ---------- import / restore (owner & admin; restore owner only — the database refuses anyone else) ---------- */

/** what is stored right now (not the on-screen copy) for the dates a file covers: ids are global, so all people */
export async function storedBetween(from: string, to: string): Promise<{ex: Existing; dates: {memberId: string; date: string}[]}> {
  const rows = (await fetchAll('time_entries', 'id,member_id,work_date,started_wall,ended_wall,duration_seconds,description,billable', 'id', undefined,
    q => q.gte('work_date', from).lte('work_date', to))).map(entryFromRow);
  return {ex: indexExisting(rows), dates: rows.map(e => ({memberId: e.memberId, date: e.date}))};
}
/** does this person have any entry at all (one row is enough; RLS decides what this login may read)? the welcome asks */
export async function hasOwnEntry(memberId: string): Promise<boolean> {
  return (check(await sb.from('time_entries').select('id').eq('member_id', memberId).limit(1)) as any[]).length > 0;
}
export async function countEntries(fromDate?: string): Promise<number> {
  let q = sb.from('time_entries').select('id', {count: 'exact', head: true});
  if (fromDate) q = q.gte('work_date', fromDate);
  const {count, error} = await q;
  if (error) throw new Error(error.message);
  return count || 0;
}
async function inBatches<T>(rows: T[], size: number, fn: (b: T[]) => Promise<void>, progress?: (done: number) => void) {
  for (let i = 0; i < rows.length; i += size) { await fn(rows.slice(i, i + size)); progress?.(Math.min(i + size, rows.length)); }
}
/** Writes an import plan. Entries are inserted with "skip if this id exists", so pressing Import twice, a second tab or a
    retry after a network error can never create a copy. Returns how many entries were actually new. */
/** A batch the database refuses (not a network problem) is written again one row at a time, so one bad row neither stops
    the rest nor disappears: every refused row comes back with the database's reason (owner decision 2026-10-13). */
async function rowByRow(rows: any[], write: (b: any[]) => Promise<{data: any; error: any; status: number}>, refused: {id: string; reason: string}[]): Promise<any[]> {
  const out: any[] = [];
  for (const r of rows) {
    const res = await write([r]);
    if (res.error) { if (isRetryable({status: res.status, message: res.error.message})) throw new Error(res.error.message); refused.push({id: r.id, reason: res.error.message}); }
    else out.push(...((res.data as any[]) || []));
  }
  return out;
}
export async function writeImport(plan: ImportPlan, say: (t: string) => void, refused: {id: string; reason: string}[] = []): Promise<number> {
  if (plan.newMembers.length) {
    say('Adding team members…');
    check(await sb.from('members').insert(plan.newMembers));
    check(await sb.from('member_cost_rates').insert(plan.newMembers.map(m => ({member_id: m.id, effective_from: BEGINNING, rate: DEFAULT_COST}))));
  }
  for (const u of plan.memberUpdates) {
    const rows = check(await sb.from('members').update({import_aliases: u.import_aliases, ...(u.email ? {email: u.email} : {})}).eq('id', u.id).select('id')) as any[];
    if (rows.length !== 1) throw new Error('Could not update a team member — your role may not allow it.');
  }
  say('Adding clients, projects and tasks…');
  if (plan.clients.length) check(await sb.from('clients').insert(plan.clients));
  if (plan.projects.length) check(await sb.from('projects').insert(plan.projects));
  if (plan.tasks.length) check(await sb.from('tasks').insert(plan.tasks));
  let written = 0;
  const put = (b: any[]) => sb.from('time_entries').upsert(b, {onConflict: 'id', ignoreDuplicates: true}).select('id') as any;
  await inBatches(plan.entries, 500, async b => {
    const res = await put(b);
    if (!res.error) { written += (res.data as any[]).length; return; }
    if (isRetryable({status: res.status, message: res.error.message})) throw new Error(res.error.message);
    written += (await rowByRow(b, put, refused)).length;   // one bad row: the others are saved, the bad one reported
  }, n => say(`Saving entries… ${n.toLocaleString('en-US')} of ${plan.entries.length.toLocaleString('en-US')}`));
  return written;
}
export async function writeWorkspace(pl: {clients: any[]; projects: any[]; rates: any[]; fills: any[]; tasks: any[]; people: {id: string; import_aliases: string[]; email?: string; billRate?: number}[]}, say: (t: string) => void) {
  say('Adding clients, projects and tasks…');
  if (pl.clients.length) check(await sb.from('clients').insert(pl.clients));
  await inBatches(pl.projects, 500, async b => { check(await sb.from('projects').insert(b)); });
  // never overwrite a rate that is set (a $0 rate is set): "skip if a rate row exists"
  await inBatches([...pl.rates, ...pl.fills], 500, async b => { check(await sb.from('project_rates').upsert(b, {onConflict: 'project_id', ignoreDuplicates: true})); });
  await inBatches(pl.tasks, 500, async b => { check(await sb.from('tasks').insert(b)); });
  for (const p of pl.people) {
    check(await sb.from('members').update({import_aliases: p.import_aliases, ...(p.email ? {email: p.email} : {})}).eq('id', p.id));
    if (p.billRate !== undefined) check(await sb.from('member_money').upsert({member_id: p.id, bill_rate: p.billRate}, {onConflict: 'member_id', ignoreDuplicates: true}));
  }
}
/** every table, read fresh, as plain rows (for backups and for checking a restore) */
export async function readTables(say?: (t: string) => void): Promise<Tables> {
  const out: any = {};
  for (const [t, cols] of Object.entries(TABLE_COLS)) {
    say?.('Reading ' + t.replace('_', ' ') + '…');
    out[t] = await fetchAll(t, cols, orderOf(t as keyof Tables),
      t === 'time_entries' ? (n, total) => say?.(`Reading time entries… ${n.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`) : undefined);
  }
  return out as Tables;
}
/** Puts every row of a backup back by its key: a row that exists in both is replaced by the backup's version; rows only in
    the database are left alone. "No rate" / "no contact" in the backup removes the rate / contact row (NULL is not 0).
    Role before archiving (v3 files): written only with archivedRole = true (the owner; the database refuses it from anyone
    else), and only for people whose stored value is missing or different (read fresh here). Returns how many were set / cleared. */
export async function writeRestore(t: Tables, say: (t: string) => void, archivedRole = false, refused: {id: string; reason: string}[] = []): Promise<{set: number; cleared: number}> {
  const up = async (table: keyof Tables, rows: any[], onConflict: string) =>
    inBatches(rows, 500, async b => {
      const res = await sb.from(table).upsert(b, {onConflict}) as any;
      if (!res.error) return;
      if (table !== 'time_entries' || isRetryable({status: res.status, message: res.error.message})) throw new Error(res.error.message);
      await rowByRow(b, x => sb.from(table).upsert(x, {onConflict}) as any, refused);   // time entries: a refused row is reported, the rest restored
    }, n => say(`Restoring ${table.replace('_', ' ')}… ${n.toLocaleString('en-US')} of ${rows.length.toLocaleString('en-US')}`));
  const del = async (table: string, col: string, ids: string[]) => inBatches(ids, 200, async b => { check(await sb.from(table).delete().in(col, b)); });
  await up('members', t.members.map(({archived_role, ...m}) => m), 'id');
  const out = {set: 0, cleared: 0};
  if (archivedRole && t.members.some(m => m.archived_role !== undefined)) {
    say('Restoring the role before archiving…');
    const plan = archivedRolePlan(t.members, await fetchAll('members', 'id,archived_role', 'id'));
    const byValue = new Map<string | null, string[]>();
    for (const c of plan.change) byValue.set(c.archived_role, [...byValue.get(c.archived_role) || [], c.id]);
    for (const [v, ids] of byValue) await inBatches(ids, 200, async b => { check(await sb.from('members').update({archived_role: v}).in('id', b)); });
    out.set = plan.toSet; out.cleared = plan.toClear;
  }
  const withMoney = new Set(t.member_money.map(r => r.member_id));
  await up('member_money', t.member_money, 'member_id');
  await del('member_money', 'member_id', t.members.map(m => m.id).filter(id => !withMoney.has(id)));
  await up('member_cost_rates', t.member_cost_rates, 'member_id,effective_from');
  for (const m of t.members) {   // the backup's cost history replaces the stored one (new rows were written first)
    const keep = t.member_cost_rates.filter(r => r.member_id === m.id).map(r => r.effective_from);
    if (keep.length) check(await sb.from('member_cost_rates').delete().eq('member_id', m.id).not('effective_from', 'in', '(' + keep.join(',') + ')'));
  }
  await up('clients', t.clients, 'id');
  const withContact = new Set(t.client_contacts.map(r => r.client_id));
  await up('client_contacts', t.client_contacts, 'client_id');
  await del('client_contacts', 'client_id', t.clients.map(c => c.id).filter(id => !withContact.has(id)));
  await up('projects', t.projects, 'id');
  const withRate = new Set(t.project_rates.map(r => r.project_id));
  await up('project_rates', t.project_rates, 'project_id');
  await del('project_rates', 'project_id', t.projects.map(p => p.id).filter(id => !withRate.has(id)));
  await up('tasks', t.tasks, 'id');
  await up('time_entries', t.time_entries, 'id');
  // project assignments: only the missing ones are added (one that is already there stays as it is). The database records
  // the person restoring as "ticked by", now (RLS: nobody can record someone else as having ticked it).
  await inBatches(t.project_members.map(a => ({project_id: a.project_id, member_id: a.member_id})), 500,
    async b => { check(await sb.from('project_members').upsert(b, {onConflict: 'project_id,member_id', ignoreDuplicates: true})); },
    n => say(`Restoring project assignments… ${n.toLocaleString('en-US')} of ${t.project_members.length.toLocaleString('en-US')}`));
  return out;
}
/** the nightly backup job writes a row here after it has checked its own copy (read-only for staff in the app) */
export async function lastNightly(): Promise<{made_at: string; entries: number} | null> {
  const r = await sb.from('backup_runs').select('made_at,entries').order('made_at', {ascending: false}).limit(1);
  if (r.error) return null;
  return (r.data as any[])[0] || null;
}

/* ---------- sync: catching up, and sending this device's queued changes ---------- */
export async function entriesChangedSince(iso: string): Promise<Entry[]> {
  return (await fetchAll('time_entries', ENTRY_COLS, 'id', undefined, q => q.gt('updated_at', iso))).map(entryFromRow);
}
export async function deletionsSince(iso: string): Promise<Deletion[]> {
  return await fetchAll('entry_deletions', 'id,deleted_at', 'id', undefined, q => q.gt('deleted_at', iso));
}
export async function newestDeletion(): Promise<string | null> {
  const r = await sb.from('entry_deletions').select('deleted_at').order('deleted_at', {ascending: false}).limit(1);
  if (r.error) throw new Error(r.error.message);
  return (r.data as any[])[0]?.deleted_at ?? null;
}
/** a failure that should be retried later (no network, timeout, server busy) */
export class Offline extends Error { retry = true; }
const fail = (r: {error: any; status: number}) => {
  if (isRetryable({status: r.status, message: r.error?.message})) throw new Offline(r.error?.message || 'network');
  throw new Error(r.error?.message || 'refused');
};
/** "Not saved": what the server has now for an entry (null = no such entry, or not readable for this login) */
export async function entryById(id: string): Promise<Entry | null> {
  const rows = check(await sb.from('time_entries').select(ENTRY_COLS).eq('id', id)) as any[];
  return rows.length ? entryFromRow(rows[0]) : null;
}
/** "Suggest a fix": does the database have a deletion note for this entry id (so the id can be used again)? RLS: staff
    read every note, anyone else their own */
export async function deletionNoted(id: string): Promise<boolean> {
  return (check(await sb.from('entry_deletions').select('id').eq('id', id)) as any[]).length > 0;
}
/** a stopped timer whose entry was refused: its time is kept in "Not saved" first, then the server's timer is removed so it
    does not look as if it were still running (best effort; refused = it simply stays) */
export async function removeTimer(memberId: string, startedAt: string): Promise<void> {
  await sb.from('active_timers').delete().eq('member_id', memberId).eq('started_at', startedAt);
}
/** what the database answered for one queued change (migration 016): its rows as they are now, ids that are gone, the
    person's running timer when it was touched (null: none; undefined: not told), and what it merged ("notes") */
export interface SendResult { status: string; entries: Entry[]; gone: string[]; timer?: Timer | null; notes: MergeNote[] }
const timerOrNull = (t: any): Timer | null => t && t.member_id ? timerFromRow(t) : null;
/** Sends one queued change through the database's merge functions (migration 016: every device's changes are applied in
    the order they were really made — the op's madeAt — atomically, under the caller's RLS). Safe to repeat: a repeat is
    recognized (same entry id / same timer start) instead of making a copy. A change queued by an older app version
    without madeAt counts as made when its timer started / stopped, anything else as made now (when it reaches the
    server). Throws Offline (retry later) or Error (refused: kept in Not saved). */
export async function sendOp(op: Op): Promise<SendResult> {
  const made = opMadeAt(op) ?? null;
  const tRow = (t: Timer) => ({member_id: t.memberId, project_id: t.projectId || null, task_id: t.taskId || null, description: t.description, billable: t.billable, started_at: t.startedAt});
  let r: {data: any; error: any; status: number};
  switch (op.kind) {
    case 'saveEntry': r = await sb.rpc('op_save_entry', {e: entryToRow(op.entry), is_new: op.isNew, made_at: made}); break;
    case 'deleteEntry': r = await sb.rpc('op_delete_entry', {entry_id: op.entryId, made_at: made}); break;
    case 'startTimer': r = await sb.rpc('op_timer_start', {t: tRow(op.timer), made_at: made}); break;
    case 'updateTimer': r = await sb.rpc('op_timer_update', {prev: tRow(op.prev), t: tRow(op.timer), made_at: made}); break;
    case 'stopTimer': r = await sb.rpc('op_timer_stop', {t: tRow(op.timer), e: entryToRow(op.entry), made_at: made}); break;
  }
  if (r.error) fail(r);
  const d = r.data || {};
  return {status: String(d.status || 'saved'), entries: (d.entries || []).map(entryFromRow), gone: d.gone || [], notes: d.notes || [],
          ...('timer' in d ? {timer: timerOrNull(d.timer)} : {})};
}

/* ---------- reports: computed by the database (the device keeps only recent weeks) ---------- */
export interface ReportFilter { from: string; to: string; members: string[]; clients: string[]; projects: string[]; tasks: string[]; tags: string[]; billable: string; minHours: string; q: string;
  tz?: string }   // tz: Reports' "My time zone" switch (absent = Chicago; migration 014)
export interface ReportResult { total: ServerTotal; unit: string; buckets: {d: string; sec: number; bill: number; n: number}[] | null; groups: ServerGroup[] | null; years: number[] | null; tags: string[] | null }
export async function report(f: ReportFilter, dims: string[], totalsOnly = false): Promise<ReportResult> {
  return check(await sb.rpc('report', {f, dims, totals_only: totalsOnly})) as ReportResult;
}
export interface ReportRow { id: string; member_id: string; project_id: string | null; task_id: string | null; description: string; billable: boolean; work_date: string;
  started_wall: string; ended_wall: string; utc_offset_min: number; duration_seconds: number; tags: string[]; source: string; bill_rate_used: number | null; cost_rate_used: number | null; rev_u: number | null; cost_u: number | null }
/** entries of one opened group (keys: e.g. {client, month}), newest first */
export async function reportEntries(f: ReportFilter, keys: Record<string, string>, lim: number, off = 0, sort = 'start', asc = false): Promise<{total: number; rows: ReportRow[]}> {
  return check(await sb.rpc('report_entries', {f, keys, lim, off, sort, asc_order: asc})) as any;
}
export interface Usage { clients: string[]; projects: string[]; tasks: string[]; members: string[] }
/** ids that have time recorded under the filter (for the menus' Active / Inactive choice) */
export async function reportUsage(f: Partial<ReportFilter>): Promise<Usage> {
  return check(await sb.rpc('report_usage', {f})) as Usage;
}
/** hours per weekday (d: 1 = Monday) and per clock hour (h: 0-23) */
export async function reportRhythm(f: ReportFilter): Promise<{dow: {d: number; sec: number; days: number}[]; hour: {h: number; sec: number}[]}> {
  return check(await sb.rpc('report_rhythm', {f})) as any;
}
/** every entry of a report (for exports), or of one opened group (keys, as reportEntries), fetched page by page */
export async function reportEntriesAll(f: ReportFilter, onProgress?: (n: number, total: number) => void, keys: Record<string, string> = {}): Promise<ReportRow[]> {
  const out: ReportRow[] = []; let total = Infinity;
  while (out.length < total) {
    const r = await reportEntries(f, keys, 5000, out.length);
    total = r.total; out.push(...r.rows); onProgress?.(out.length, total);
    if (!r.rows.length) break;
  }
  return out;
}
